/*
 * shipping-logic-se-fi.js
 * Sweden / Denmark / Finland delivery estimate + order cutoff countdown.
 *
 * Idempotent by design. A single 1s tick re-reads the DOM and rewrites every
 * widget instance it finds. Combined-listing colour switches replace the
 * product section, so anything that binds once to a captured node freezes.
 * Nothing in here holds a node reference between ticks.
 *
 * Market differences are driven by data-shipping-country on .delivery__container,
 * stamped by snippets/shipping-widget-pdp.liquid. Sweden delivers on Saturdays
 * (Instabox), so Sweden gets a Mon-Sat delivery calendar and keeps the widget
 * visible on Friday before cutoff. Denmark and Finland keep the original
 * Mon-Fri behaviour and stay hidden on Fridays.
 *
 * Dispatch is always Mon-Fri regardless of market: the warehouse is Swedish and
 * closed at weekends. Only the DELIVERY calendar differs.
 */
(function () {
  if (window.__npShippingSeFi) return;
  window.__npShippingSeFi = true;

  var ZONE = 'Europe/Stockholm';
  var CUTOFF_HOUR = 17;
  var SATURDAY_DELIVERY_MARKETS = ['Sweden'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // [month, day] pairs. NOTE: the Easter-dependent entries below are 2026 dates.
  // They need updating each year, same as the original file.
  var HOLIDAYS = [
    [1, 1],   // New Year's Day
    [1, 6],   // Epiphany
    [4, 2],   // Thu before Good Friday
    [4, 3],   // Good Friday
    [4, 4],   // Sat before Easter
    [4, 5],   // Easter Sunday
    [4, 6],   // Easter Monday
    [5, 1],   // May Day
    [5, 14],  // Ascension Day
    [5, 24],  // Whit Sunday
    [6, 6],   // National Day
    [6, 19],  // Midsummer Eve
    [6, 20],  // Midsummer Day
    [10, 31], // All Saints' Day
    [12, 24], // Christmas Eve
    [12, 25], // Christmas Day
    [12, 26], // 2nd Day of Christmas
    [12, 31]  // New Year's Eve
  ];

  var BLACKOUT = [[4, 2], [4, 3], [4, 4], [4, 5], [4, 6], [5, 14], [6, 19]];

  // Holiday maths runs once per day rather than once per second.
  var cache = { key: null, data: null };

  function matches(list, d) {
    for (var i = 0; i < list.length; i++) {
      if (d.month === list[i][0] && d.day === list[i][1]) return true;
    }
    return false;
  }

  function isHoliday(d) { return matches(HOLIDAYS, d); }
  function isBlackout(d) { return matches(BLACKOUT, d); }

  // Swedish warehouse, closed weekends: dispatch is Mon-Fri in every market.
  function nextDispatchDay(d) {
    do { d = d.plus({ days: 1 }); } while (d.weekday > 5 || isHoliday(d));
    return d;
  }

  // Carrier-dependent: Instabox delivers Saturdays in Sweden, Bring does not in
  // Denmark. Sunday is never a delivery day.
  function nextDeliveryDay(d, saturdayDelivery) {
    var lastDay = saturdayDelivery ? 6 : 5;
    do { d = d.plus({ days: 1 }); } while (d.weekday > lastDay || isHoliday(d));
    return d;
  }

  // Liquid knows the country for certain; the JS should not try to guess it.
  function market() {
    var el = document.querySelector('.delivery__container[data-shipping-country]');
    return el ? el.getAttribute('data-shipping-country') : '';
  }

  function hasSaturdayDelivery(country) {
    return SATURDAY_DELIVERY_MARKETS.indexOf(country) !== -1;
  }

  function computeDay(now, saturdayDelivery) {
    var hour = now.hour;
    var day = now.weekday; // 1 = Mon ... 7 = Sun
    var shippingDate;

    if (day <= 5 && hour < CUTOFF_HOUR && !isHoliday(now)) {
      shippingDate = now;
    } else {
      shippingDate = nextDispatchDay(now);
    }

    var deliveryDate = nextDeliveryDay(shippingDate, saturdayDelivery);

    function label(d) {
      if (d.hasSame(now, 'day')) return 'Today';
      if (d.hasSame(now.plus({ days: 1 }), 'day')) return 'Tomorrow';
      return MONTHS[d.month - 1] + ' ' + d.day;
    }

    // Sweden now shows on Friday BEFORE cutoff, because the message is a good
    // one there: order today, delivered tomorrow (Saturday) by Instabox. After
    // cutoff on Friday the honest answer is Mon/Tue, so it stays hidden as
    // before. Denmark and Finland hide all day Friday, unchanged.
    var hideOnFriday = day === 5 && (!saturdayDelivery || hour >= CUTOFF_HOUR);

    return {
      shipping: label(shippingDate),
      delivery: label(deliveryDate),
      hideWidget: hideOnFriday || isBlackout(now)
    };
  }

  function cutoffTarget(now) {
    var base = now.hour >= CUTOFF_HOUR ? now.plus({ days: 1 }) : now;
    return base.set({ hour: CUTOFF_HOUR, minute: 0, second: 0, millisecond: 0 });
  }

  function pad(n) {
    return String(Math.max(0, Math.floor(n || 0))).padStart(2, '0');
  }

  function setAll(selector, value) {
    var nodes = document.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].textContent !== value) nodes[i].textContent = value;
    }
  }

  function tick() {
    if (!window.luxon) return;

    var now = luxon.DateTime.now().setZone(ZONE);
    var country = market();
    var saturdayDelivery = hasSaturdayDelivery(country);

    var key = now.toISODate() + (now.hour >= CUTOFF_HOUR ? 'B' : 'A') + '|' + country;
    if (cache.key !== key) {
      cache.key = key;
      cache.data = computeDay(now, saturdayDelivery);
    }
    var day = cache.data;

    var containers = document.querySelectorAll('.delivery__container');
    for (var i = 0; i < containers.length; i++) {
      containers[i].style.display = day.hideWidget ? 'none' : 'block';
    }

    setAll('.date__estimated', day.shipping);
    setAll('.date__delivery', day.delivery);

    var remaining = cutoffTarget(now).diff(now, ['hours', 'minutes', 'seconds']).toObject();
    var text = pad(remaining.hours) + ':' + pad(remaining.minutes) + ':' + pad(remaining.seconds);
    var hideCountdown = now.weekday === 5 && now.hour >= CUTOFF_HOUR;

    var timers = document.querySelectorAll('.countdown__timer');
    for (var j = 0; j < timers.length; j++) {
      timers[j].style.display = hideCountdown ? 'none' : '';
      var timeEl = timers[j].querySelector('.countdown__time');
      if (timeEl && timeEl.textContent !== text) timeEl.textContent = text;
    }
  }

  // Immediate repaint on swap so there is no sub-second blank, on top of the
  // 1s tick that would catch it anyway.
  var pending = false;
  function scheduleTick() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      tick();
    });
  }

  function watchForSwaps() {
    if (typeof MutationObserver !== 'function') return;

    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if ((node.matches && node.matches('.delivery__container')) ||
              (node.querySelector && node.querySelector('.delivery__container'))) {
            scheduleTick();
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  function boot() {
    if (!window.luxon) {
      setTimeout(boot, 50);
      return;
    }
    tick();
    setInterval(tick, 1000);
    watchForSwaps();
    ['shopify:section:load', 'popstate', 'pageshow'].forEach(function (evt) {
      window.addEventListener(evt, scheduleTick);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
